import React, { useState, useEffect } from "react";
import { Tooltip, Button, Input, Modal, Table, Select, message, DatePicker } from "antd";
import { FiGrid } from "react-icons/fi";
import { FaTableList } from "react-icons/fa6";
import axios from "axios";
import useAuth from "../hooks/useAuth";
import CandidateCard from "../components/CandidateCard";
import CandidateTable from "../components/CandidateTable";
import Registration from "../components/Registration";
import Hotpicks from "../components/Hotpicks";
import * as XLSX from "xlsx";
import ToBeJoinedCandidates from '../components/ToBeJoinedCandidates';

const URL = process.env.REACT_APP_API_URL;
const { Option } = Select;
const { RangePicker } = DatePicker;

const Hr = () => {
  const [view, setView] = useState("table");
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isTotalModalVisible, setIsTotalModalVisible] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterOption, setFilterOption] = useState("All");
  const { auth, token } = useAuth();
  const [candidates, setCandidates] = useState([]);

  const [selectedDateRange, setSelectedDateRange] = useState([]);
  // Applied date range that triggers fetch
  const [appliedDateRange, setAppliedDateRange] = useState([]);

  const showModal = () => {
    setIsModalVisible(true);
  };

  const closeModal = () => {
    setIsModalVisible(false);
  };

  const showTotalModal = () => {
    setIsTotalModalVisible(true);
  };

  const closeTotalModal = () => {
    setIsTotalModalVisible(false);
  };

  // const toggleView = () => {
  //   setView(view === "tile" ? "table" : "tile");
  // };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const toggleView = (newView) => {
    setView(newView);
  };

  const handleDownload = () => {
    const filteredCandidatesForDownload = candidates.map(({ confirmPassword, history, round, resume, image, notification, role, empCount, _id, password, __v, roleId, ...rest }) => rest);
    const worksheet = XLSX.utils.json_to_sheet(filteredCandidatesForDownload);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Candidates");
    XLSX.writeFile(workbook, "candidates.xlsx");
  };


  const getAgeing = (postedAt) => {
    if (!postedAt) return "";
    const postedDate = new Date(postedAt);
    const today = new Date();

    const diffTime = today - postedDate; // milliseconds difference
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24)); // convert to days

    return diffDays;
  };

  const handleDownloadOpenJobs = async () => {
    try {
      // 1️⃣ Fetch jobs
      const jobsResponse = await fetch(`${URL}/api/jobs`, {
        headers: {
          Authorization: `Bearer ${token}`, // ✅ use token from useAuth
        },
      });
      const jobs = await jobsResponse.json();

      if (!Array.isArray(jobs) || jobs.length === 0) {
        message.warning("No jobs found");
        return;
      }

      // 2️⃣ Filter open jobs
      const openJobs = jobs.filter(
        (job) =>
          job.status &&
          job.status.toLowerCase() !== "fulfilled" &&
          job.status.toLowerCase() !== "closed" &&
          job.status.toLowerCase() !== "closed from backend" &&
          job.status.toLowerCase() !== "req on hold" &&
          job.status.toLowerCase() !== "closed" &&
          job.status.toLowerCase() !== "closed from backend" &&
          job.status.toLowerCase() !== "req on hold"
      );

      if (openJobs.length === 0) {
        message.warning("No open jobs found");
        return;
      }

      // 3️⃣ Fetch candidates
      const candidatesResponse = await fetch(`${URL}/api/candidatesconsolidatedreport`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const candidates = await candidatesResponse.json();

      if (!Array.isArray(candidates)) {
        throw new Error("Candidates API did not return an array");
      }

      // 4️⃣ Helper function: count by substring (for includes)
      const countByStatus = (patterns) => {
        const patternList = Array.isArray(patterns) ? patterns : [patterns];
        return candidates.reduce((acc, candidate) => {
          if (candidate.status && patternList.some((p) => candidate.status.toLowerCase().includes(p.toLowerCase())) && (candidate.jobId)) {
            const id = candidate.jobId;
            acc[id] = (acc[id] || 0) + 1;
          }
          return acc;
        }, {});
      };

      // 5️⃣ Counts
      const cvSharedCount = countByStatus("cv sourced");
      const shortlistedByJob = countByStatus("cv shortlisted");
      // console.log('shortlistedByJob:', shortlistedByJob);

      const feedbackAwaitedByJob = countByStatus("cv feedback awaited");
      const rejectedByJob = countByStatus("cv rejected");
      const backoutByJob = countByStatus([
        "candidate backout",
        "candidate declined / backout",
      ]);
      const onHoldByJob = countByStatus("cv on hold"); // ✅ matches "CV on Hold"
      const testShortlisted = countByStatus("test shortlisted") || 0;
      const testRejected = countByStatus("test Rejected") || 0;
      // const l1Cleared = countByStatus("l1-hr interview cleared");
      // const l1OnHold = countByStatus("l1-hr interview hold");
      // const l1Rejected = countByStatus("l1-hr interview rejected");
      // const l2Process = countByStatus(["l2 interview cleared", "l2 interview hold"]);
      const l2Cleared = countByStatus("l2 interview cleared") ?? 0;
      const l2OnHold = countByStatus("l2 interview hold") ?? 0;
      const l2Rejected = countByStatus("l2 interview rejected") ?? 0;
      // const l3Process = countByStatus(["l3 interview cleared", "l3 interview hold"]);
      const l3Cleared = countByStatus("l3 interview cleared") ?? 0;
      const l3OnHold = countByStatus("l3 interview hold") ?? 0;
      const l3Rejected = countByStatus("l3 interview rejected") ?? 0;
      // const  completeDocumentation= countByStatus("complete documentation");
      // const  incompleteDocumentation= countByStatus("incomplete documentation");
      // const  documentationOnHold= countByStatus(["documentation on hold", "requested for documentation"]);
      // const documentationProcess = countByStatus(["complete documentation", "incomplete documentation", "documentation on hold", "requested for documentation"]);
      // const salaryNegotiotionProcess = countByStatus(["salary approved", "salary approval revoke", "salary approval on hold", "shared for negotiation"]);
      // const offerProcess = countByStatus(["offer letter shared", "offer accepted", "offer declined", "offer on hold"]);
      const offerLetterShared = countByStatus("offer letter shared");
      const toJoin = countByStatus("to join");
      const offerStage = countByStatus("offer on hold");
      const joined = countByStatus("joined");
      const offerDeclined = countByStatus("offer declined");
      // const joiningStatus = countByStatus(["joined", "candidate declined / backout", "to join", "offer revoked", "blacklisted"]);



      // 6️⃣ Format jobs for Excel
      const formattedJobs = openJobs.map((job, index) => ({
        "Sr.no": index + 1,
        Position: job.position || "",
        "Job ID": job.positionId || "",
        "Vacancies Logged": job.vacancies || "", //vacancy column added        
        "Vacancies To Be Fulfilled": (job.vacancies || 0) - (joined[job.positionId] || 0), //to be fulfilled column added
        Ageing: getAgeing(job.postedAt),
        Location: job.jobLocation || "",
        Requestor_Name: job.postedBy || "",
        "CV Shared Count": cvSharedCount[job.positionId],
        "CV Shortlisted": shortlistedByJob[job.positionId] || '',
        "CV Feedback Awaited": feedbackAwaitedByJob[job.positionId] || '',
        "CV Rejected": rejectedByJob[job.positionId] || '',
        "Candidate Backout": backoutByJob[job.positionId] || '',
        "CV On Hold": onHoldByJob[job.positionId] || '', 
        'Total Test Conducted': ((testShortlisted[job.positionId] || 0) + (testRejected[job.positionId] || 0)),
        "Test Shortlisted": testShortlisted[job.positionId] || 0,
        "Test Rejected": testRejected[job.positionId] || 0,
        // "L1-HR Cleared": l1Cleared[job.positionId] || '',
        // "L1-HR On Hold": l1OnHold[job.positionId] || '',
        // "L1-HR Rejected": l1Rejected[job.positionId] || '',
        // "L2 Process": l2Process[job.positionId] || '',
        "Total L2 Interviewed": ((l2Cleared[job.positionId] ?? 0) + (l2OnHold[job.positionId] ?? 0) + (l2Rejected[job.positionId] ?? 0)),
        "L2 Interview Cleared": l2Cleared[job.positionId] || 0,
        "L2 Interview On Hold": l2OnHold[job.positionId] || 0,
        "L2 Interview Rejected": l2Rejected[job.positionId] || 0,
        // "L3 Process": l3Process[job.positionId] || '',
        "Total L3 Interviewed": ((l3Cleared[job.positionId] ?? 0) + (l3OnHold[job.positionId] ?? 0) + (l3Rejected[job.positionId] ?? 0)),
        "L3 Interview Cleared": l3Cleared[job.positionId] || 0,
        "L3 Interview On Hold": l3OnHold[job.positionId] || 0,
        "L3 Interview Rejected": l3Rejected[job.positionId] || 0,
        // "Documentation Process": documentationProcess[job.positionId] || '',
        // "Salary Negotiation Stage": salaryNegotiotionProcess[job.positionId] || '',
        // "Offer Stage": offerProcess[job.positionId] || '',
        // "Joining Status": joiningStatus[job.positionId] || '',
        Offered: offerLetterShared[job.positionId] || '',
        "To Join": toJoin[job.positionId] || '',
        "Offer Stage": offerStage[job.positionId] || '',
        Joined: joined[job.positionId] || '',
        "Offer Decline": offerDeclined[job.positionId] || '',
      }));

      // 7️⃣ Export to Excel
      const worksheet = XLSX.utils.json_to_sheet(formattedJobs);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Open Jobs");
      XLSX.writeFile(workbook, "Open_Jobs.xlsx");
    } catch (error) {
      console.error("❌ Error downloading open jobs:", error);
      message.error("Failed to download open jobs");
    }
  };

  const handleDownloadHrData = async () => {
    try {
      const response = await axios.get(`${URL}/api/hrs-data`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const hrData = response.data;

      if (!hrData || hrData.length === 0) {
        message.warning("No HR Data Found");
        return;
      }

      let finalRows = [];
      let sr = 1;

      hrData.forEach((hr) => {
        const hasJobs = hr.details && hr.details.length > 0;

        if (!hasJobs) return;

        hr.details.forEach((job, idx) => {
          finalRows.push({
            "Sr No.": idx === 0 ? sr : "",
            "HR Name": idx === 0 ? hr.fullName : "",
            "Position / Job ID": `${job.jobPosition} (${job.jobId})`,
            "Ageing": getAgeing(job.postedAt),
            "HR Email": idx === 0 ? hr.email : "",
            "HR Status": idx === 0 ? hr.hrStatus : "",


            // Hiring details
            "CV Shared Count": job.hiringDetails["CV Sourced"] || 0,
            "CV Shortlisted": job.hiringDetails["CV Shortlisted"] || 0,
            "CV Feedback Awaited": job.hiringDetails["L2 Feedback Awaited"] || 0,
            "CV Rejected": job.hiringDetails["CV Rejected"] || 0,
            "CV on Hold": job.hiringDetails["Offer On Hold"] || 0,
            "Candidate Backout": job.hiringDetails["Candidate Backout"] || 0,

            "Total Test Conducted":
              (job.hiringDetails["Test Shortlisted"] || 0) +
              (job.hiringDetails["Test Rejected"] || 0),

            "Test Shortlisted": job.hiringDetails["Test Shortlisted"] || 0,
            "Test Rejected": job.hiringDetails["Test Rejected"] || 0,

            "Total L2 Interviewed":
              (job.hiringDetails["L2 Interview Cleared"] || 0) +
              (job.hiringDetails["L2 Interview Rejected"] || 0) +
              (job.hiringDetails["L2 Feedback Awaited"] || 0),

            "L2 Interview Cleared": job.hiringDetails["L2 Interview Cleared"] || 0,
            "L2 Interview Rejected": job.hiringDetails["L2 Interview Rejected"] || 0,
            "L2 Interview On Hold": job.hiringDetails["L2 Feedback Awaited"] || 0,

            "Total L3 Interviewed":
              (job.hiringDetails["L3 Interview Cleared"] || 0) +
              (job.hiringDetails["L3 Interview Rejected"] || 0) +
              (job.hiringDetails["L3 Feedback Awaited"] || 0),

            "L3 Interview Cleared": job.hiringDetails["L3 Interview Cleared"] || 0,
            "L3 Interview Rejected": job.hiringDetails["L3 Interview Rejected"] || 0,
            "L3 Interview On Hold": job.hiringDetails["L3 Feedback Awaited"] || 0,

            "Offered": job.hiringDetails["Offered"] || 0,
            "To Join": job.hiringDetails["To Join"] || 0,
            "Offer Stage": job.hiringDetails["Offer Stage"] || 0,
            "Joined": job.hiringDetails["Joined"] || 0,
            "Offer Decline": job.hiringDetails["Offer Declined"] || 0,
          });
        });
        sr++; // Increase Sr No. for next HR
      });

      const worksheet = XLSX.utils.json_to_sheet(finalRows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "HR Jobs View");

      XLSX.writeFile(workbook, "HR_Jobs_Detailed_Report.xlsx");
    } catch (error) {
      console.error("Error downloading report:", error);
      message.error("Failed to download HR report");
    }
  };


  // useEffect(() => {
  //   const fetchJobs = async () => {
  //     try {
  //       const response = await axios.get(
  //         `${URL}/api/candidatesreport`,
  //         {
  //           headers: {
  //             Authorization: `Bearer ${token}`,
  //           },
  //         }
  //       );
  //       setCandidates(response.data.reverse());
  //     } catch (error) {
  //       console.error("Error fetching jobs:", error);
  //     }
  //   };
  //   fetchJobs();
  // }, []);

  //Added from CTS-JULY
  useEffect(() => {
    const fetchJobs = async () => {
      try {
        // const includeJoined = filterOption === "Joined";
        const apiUrl = `${URL}/api/candidatesreport`;

        const params = {};

        if (appliedDateRange.length === 2) {
          params.startDate = appliedDateRange[0].format("YYYY-MM-DD");
          params.endDate = appliedDateRange[1].format("YYYY-MM-DD");
        }

        const response = await axios.get(apiUrl, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params,
        });

        setCandidates(response.data.reverse());

        console.log(
          "🎯 Total matching candidates (career or Career):",
          response.data.filter(c => ["career", "Career"].includes(c.source?.trim())).length
        );

      } catch (error) {
        console.error("Error fetching jobs:", error);
      }
    };

    fetchJobs();
  }, [filterOption, token, appliedDateRange]);


  const filteredCandidates = candidates.filter((candidate) => {
    const matchesSearchQuery = candidate.fullName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      candidate.status?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      candidate.mgrName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      candidate.position?.toLowerCase().includes(searchQuery.toLowerCase());

    //   const matchesFilterOption = (filterOption === "All") ||
    //     (filterOption === "Campus Drive" && candidate.isCampusDrive) ||
    //     (filterOption === "Bulk Upload" && candidate.isBulkUploadData && !candidate.isCampusDrive) ||
    //     (filterOption === "CTS" && !candidate.isBulkUploadData && !candidate.isCampusDrive);
    //   return matchesSearchQuery && matchesFilterOption
    // });
    const matchesFilterOption = (
      filterOption === "All" ||
      (filterOption === "Campus Drive" && candidate.isCampusDrive) ||
      (filterOption === "Bulk Upload" && candidate.isBulkUploadData && !candidate.isCampusDrive) ||
      (filterOption === "CTS" && !candidate.isBulkUploadData && !candidate.isCampusDrive) ||
      (filterOption === "Joined" && candidate.status === "Joined") ||
      (filterOption === "Careers" && candidate.source?.trim().toLowerCase() === "career")
    );

    return matchesSearchQuery && matchesFilterOption;
  });

  const columns = [
    { title: 'Full Name', dataIndex: 'fullName', key: 'fullName' },
    { title: 'Position', dataIndex: 'position', key: 'position' },
    { title: 'Status', dataIndex: 'status', key: 'status' },
  ];

  const handleCandidateUpdate = (updatedCandidate) => {
    setCandidates(prevCandidates =>
      prevCandidates.map(candidate =>
        candidate._id === updatedCandidate._id ? updatedCandidate : candidate
      )
    );
  };

  return (
    <div className="table-container">
      <div className="topContainer">
        <div className="add-button-container" style={{ display: "flex", gap: "10px", alignItems: "center" }}>
          <Tooltip title="Add Applicant" color="cyan">
            <Button onClick={showModal} type="text" className="add-button">
              Add New Candidate
            </Button>
          </Tooltip>
          <Button
            type="primary"
            danger
            onClick={handleDownloadOpenJobs}
          >
            Download Open Jobs
          </Button>
          <Button
            type="primary"
            danger
            style={{ marginLeft: '7px' }}
            onClick={handleDownloadHrData}
          >
            Download HR Data
          </Button>

          <Select
            defaultValue="All"
            style={{ width: 200 }}
            onChange={setFilterOption}
          >
            <Option value="All">All</Option>
            <Option value="Campus Drive">Campus Drive</Option>
            <Option value="Bulk Upload">Bulk Upload</Option>
            <Option value="CTS">CTS</Option>
            <Option value="Joined">Joined</Option>
            <Option value="Careers">Careers</Option>
          </Select>
          {/* Date Range Picker */}
          <RangePicker
            onChange={(dates) => setSelectedDateRange(dates || [])}
            value={selectedDateRange}
          />
          <Button
            type="primary"
            onClick={() => setAppliedDateRange(selectedDateRange)}
            disabled={selectedDateRange.length !== 2}
          >
            Search
          </Button>
        </div>

        <div className="search-toggle">
          {view === "tile" && (
            <Input
              placeholder="Search by Name, Position, Status"
              value={searchQuery}
              onChange={handleSearch}
              className="ant-searchIn"
            />
          )}
          {/* <div className="toggle-button">
            <Button
              onClick={() => toggleView("tile")}
              type="text"
              icon={<FiGrid />}
              className={view === "tile" ? "active-button" : ""}
            >
              Tile View
            </Button>
            <span className='btn-divider'>&nbsp; | &nbsp;</span>
            <Button onClick={toggleView} type='text' icon={<FaTableList />} className={view === 'table' ? 'active-button' : ''}>
              Grid View
            </Button>
          </div> */}
          <div className="toggle-button">
            <Button
              onClick={() => toggleView("tile")}
              type="text"
              icon={<FiGrid />}
              className={view === "tile" ? "active-button" : ""}
            >
              Tile View
            </Button>

            <span className='btn-divider'>&nbsp; | &nbsp;</span>

            <Button
              onClick={() => toggleView("table")}
              type="text"
              icon={<FaTableList />}
              className={view === "table" ? "active-button" : ""}
            >
              Grid View
            </Button>
          </div>
        </div>
      </div>
      <br />
      <div>
        {view === "tile" ? (
          <CandidateCard candidates={filteredCandidates} onUpdate={handleCandidateUpdate} setCandidates={setCandidates} />
        ) : (
          //<CandidateTable auth={auth} filterOption={filterOption} />
          <CandidateTable
            auth={auth}
            filterOption={filterOption}
            candidates={filteredCandidates}
          />

        )}
      </div>
      <div>
        <Hotpicks />
        <ToBeJoinedCandidates />
      </div>
      <Modal
        open={isModalVisible}
        onCancel={closeModal}
        footer={null}
        width={800}
        title={<h2>Add New Applicant</h2>}
      >
        <Registration closeModal={closeModal} />
      </Modal>
      <Modal
        open={isTotalModalVisible}
        onCancel={closeTotalModal}
        footer={[
          <Button key="download" onClick={handleDownload}>
            Download
          </Button>,
          <Button key="close" onClick={closeTotalModal}>
            Close
          </Button>,
        ]}
        width={1000}
      >
        <Table dataSource={candidates} columns={columns} rowKey="_id" />
      </Modal>
    </div>
  );
};

export default Hr;
